import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowDown,
  Droplets,
  ClipboardList,
  MessageSquareWarning,
  PencilRuler,
  Truck,
  PackageCheck,
  ShoppingCart,
  ScanFace,
  Receipt,
  FileSpreadsheet,
  ShieldCheck,
  IdCard,
  BrainCircuit,
  BarChart3,
  Radar,
  LineChart,
  HardHat,
  Building2,
  Wallet,
  MessageCircle,
  Users,
  Mail,
  FolderOpen,
  CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { AnimatedSection, AnimatedContainer, AnimatedItem } from "@/components/AnimatedSection";
import SEO, { SITE_URL, breadcrumbs } from "@/components/SEO";

const PATH = "/industries/construction";
const IMG = "/industries-images/construction";

const heroStats = [
  { val: "16", label: "Construction and real estate workflows we automate" },
  { val: "100+", label: "Site WhatsApp groups already monitored by our agents" },
  { val: "3x", label: "Daily curing checks, verified from site photos" },
  { val: "85%", label: "Less manual data entry across invoices, POs and challans" },
];

const levers = [
  {
    icon: HardHat,
    title: "Site execution",
    body: "Progress, curing, work plans and client escalations live in dozens of WhatsApp groups. Nobody has one reliable view of what actually happened on site today.",
  },
  {
    icon: Wallet,
    title: "Back office and compliance",
    body: "Vehicle logs, goods receipts, invoices, POs, challans, RERA and approval papers arrive in every format and are re-typed by hand into registers and the ERP.",
  },
  {
    icon: Building2,
    title: "Leadership visibility",
    body: "Reports are compiled from Excel at the end of the week, and the knowledge behind every project leaves with the engineer or coordinator who moves on.",
  },
];

const sources = [
  { icon: MessageCircle, name: "WhatsApp", desc: "Site, project and vendor groups, photos and voice notes" },
  { icon: Users, name: "Microsoft Teams", desc: "Chats, channels, meeting notes and shared files" },
  { icon: Mail, name: "Email", desc: "Vendor quotes, approvals, client and authority mail" },
  { icon: FolderOpen, name: "Files", desc: "Excel trackers, POs, invoices, drawings and scanned PDFs" },
];

const stages = [
  {
    id: "site",
    label: "Site execution",
    kicker: "Workflows 01 to 04",
    title: "An AI project manager inside every site WhatsApp group",
    intro:
      "Your site teams already report on WhatsApp. Four workflows that read every message and photo, verify the work and turn the chat into trackers leadership can trust.",
    image: `${IMG}/site.jpg`,
    offerings: [
      {
        num: "01",
        icon: Droplets,
        title: "Curing verification from site photos",
        problem: "Curing has to happen three times a day, and the only proof is a photo posted in a busy group that nobody has time to check.",
        build:
          "An agent in every project group that picks up each curing photo and message, uses a vision-language model to confirm curing is really happening, and logs every project and time slot as Done, In Progress, Missed or Not Applicable, with low-confidence cases sent to a person.",
        outcomes: ["100% of curing cycles logged", "Missed cycles flagged the same day", "Photo evidence for every entry"],
      },
      {
        num: "02",
        icon: ClipboardList,
        title: "Daily progress and work plan extraction",
        problem: "Coordinators read every group each evening and type up what was planned and done, project by project.",
        build:
          "Site engineers keep posting as they do today. The agent extracts each day's work plan and progress by tower, floor and activity, flags incomplete plans and writes one project-wise tracker for the operations team.",
        outcomes: ["One tracker across all sites", "No evening data entry", "Incomplete plans flagged"],
      },
      {
        num: "03",
        icon: MessageSquareWarning,
        title: "Client escalation and PM reply tracking",
        problem: "Complaints about delays, quality and payments get buried in group chats, and nobody knows how long the client waited for an answer.",
        build:
          "Every client escalation detected and categorised by severity, the PM's reply matched to it and the response time measured, with an escalation register and alerts when no one has replied within the agreed window.",
        outcomes: ["No escalation lost in a thread", "Response time per PM", "Alerts before clients chase"],
      },
      {
        num: "04",
        icon: PencilRuler,
        title: "Change requests and material finalisation",
        problem: "Buyers and site teams agree changes and finalise tiles, fittings and finishes over chat, and the decision is hard to find at handover.",
        build:
          "Change requests captured the moment they are asked, routed for approval and costing, and every finalised material logged with the exact photo that was approved, linked to the unit and project.",
        outcomes: ["Every change on record", "Approved photo per material", "Fewer handover disputes"],
      },
    ],
  },
  {
    id: "logistics",
    label: "Site logistics and procurement",
    kicker: "Workflows 05 to 08",
    title: "Gate, stores and labour records that fill themselves",
    intro:
      "Every truck, delivery, order and worker creates paperwork that someone keys in later. Four workflows that capture it once, at the source.",
    image: `${IMG}/logistics.jpg`,
    offerings: [
      {
        num: "05",
        icon: Truck,
        title: "Vehicle entry and gate logging",
        problem: "Gate staff write vehicle entries in registers that are typed up days later, if at all.",
        build:
          "A photo of the number plate or challan on WhatsApp becomes a structured gate entry with vehicle, vendor, material, time and site, matched to the expected deliveries for the day.",
        outcomes: ["Digital gate register", "Unplanned vehicles flagged", "No end-of-day typing"],
      },
      {
        num: "06",
        icon: PackageCheck,
        title: "Goods receipt and challan matching",
        problem: "Delivery challans are re-keyed into the GRN register, and short or wrong deliveries are found when the invoice arrives.",
        build:
          "Each challan read on arrival, quantities and specifications matched against the purchase order, and short deliveries or mismatches flagged to stores and procurement before the truck leaves.",
        outcomes: ["GRNs created automatically", "Short deliveries caught at the gate", "Clean data for invoice matching"],
      },
      {
        num: "07",
        icon: ShoppingCart,
        title: "Site equipment and material ordering",
        problem: "Site requests for equipment and materials go by phone call, so the wrong item or quantity often arrives.",
        build:
          "A simple catalogue and ordering flow on WhatsApp for site teams, with photos and specifications, approvals by the project head in the chat and orders sent straight to procurement.",
        outcomes: ["Right item ordered first time", "Approvals inside WhatsApp", "Every request tracked"],
      },
      {
        num: "08",
        icon: ScanFace,
        title: "Workforce attendance at remote sites",
        problem: "Labour attendance at remote sites is marked on paper or with devices that need a reliable connection.",
        build:
          "Face-recognition attendance that runs on site without needing the cloud, syncing to payroll and contractor billing when a connection is available.",
        outcomes: ["Proxy attendance removed", "Works offline", "Feeds contractor billing"],
      },
    ],
  },
  {
    id: "office",
    label: "Back office and compliance",
    kicker: "Workflows 09 to 12",
    title: "Invoices, contractors and approvals without the re-typing",
    intro:
      "Real estate runs on paper from hundreds of vendors and authorities. Four workflows that read it, check it and post it, with people approving only exceptions.",
    image: `${IMG}/office.jpg`,
    offerings: [
      {
        num: "09",
        icon: Receipt,
        title: "Invoice and PO processing",
        problem: "Every vendor and subcontractor invoice comes in a different format and is typed into the ERP by hand.",
        build:
          "Document AI that reads invoices in any format, runs a three-way match against the PO and goods receipt, checks GST and posts approved entries to SAP, Tally or your ERP. Only mismatches go to a person.",
        outcomes: ["85% less manual data entry", "Three-way match on every invoice", "Invoice backlog cleared"],
      },
      {
        num: "10",
        icon: FileSpreadsheet,
        title: "Contractor billing and reporting",
        problem: "Contractor progress and running bills are chased over calls and checked line by line against measurement sheets.",
        build:
          "Contractor updates captured as they are posted, running bills checked against measured work, attendance and the work order, and gaps chased automatically before the bill reaches accounts.",
        outcomes: ["Bills checked against site data", "Fewer billing disputes", "Contractor reports on time"],
      },
      {
        num: "11",
        icon: ShieldCheck,
        title: "RERA, approvals and compliance documents",
        problem: "RERA filings, sanctions, NOCs and occupancy certificates sit across inboxes and folders, and deadlines are tracked by memory.",
        build:
          "Every approval and regulatory document read and indexed by project, with key dates, conditions and obligations extracted into a compliance calendar and reminders before anything lapses.",
        outcomes: ["One compliance calendar", "No missed deadlines", "Conditions tracked per project"],
      },
      {
        num: "12",
        icon: IdCard,
        title: "Buyer KYC and sales paperwork",
        problem: "Booking forms, KYC documents and agreements are checked and keyed in by the CRM team for every unit sold.",
        build:
          "Buyer documents read and validated as they arrive, details filled into the CRM and agreement drafts, and missing or mismatched documents flagged to the sales team.",
        outcomes: ["Faster bookings to agreement", "Fewer data entry errors", "Complete files at handover"],
      },
    ],
  },
  {
    id: "leadership",
    label: "Leadership and knowledge",
    kicker: "Workflows 13 to 16",
    title: "One brain for the whole company, and reports that write themselves",
    intro:
      "Everything above feeds one knowledge source. Four workflows that turn it into answers, reports and early warnings for leadership.",
    image: `${IMG}/leadership.jpg`,
    offerings: [
      {
        num: "13",
        icon: BrainCircuit,
        title: "Company knowledge AI",
        problem: "Finding one decision means asking three people, and when someone leaves, the context of their projects leaves with them.",
        build:
          "WhatsApp groups, Teams, email and files connected into one searchable knowledge source. Anyone can ask a question on WhatsApp, Teams or the web and get an answer with the source message, email or file attached, following your existing access rules.",
        outcomes: ["Answers with sources", "New joiners productive on day one", "Knowledge stays with the company"],
      },
      {
        num: "14",
        icon: BarChart3,
        title: "Executive reporting and daily brief",
        problem: "Analysts merge Excel sheets from every site to build weekly reports that are out of date when they arrive.",
        build:
          "Site, procurement, finance and sales data compiled automatically into a live leadership dashboard and a written brief every morning, with exceptions at the top.",
        outcomes: ["Report ready every morning", "No Excel compilation", "Exceptions surfaced first"],
      },
      {
        num: "15",
        icon: Radar,
        title: "Project control tower and delay risk",
        problem: "Delays are noticed when a milestone is missed, not when the material shortage or labour gap that caused it first appeared.",
        build:
          "Milestones, procurement status, deliveries, labour and quality sign-offs tracked across every project, with a model that flags handover risk early and explains the cause in plain language.",
        outcomes: ["Risk flagged weeks earlier", "Cause explained, not just scored", "One view across projects"],
      },
      {
        num: "16",
        icon: LineChart,
        title: "Sales and marketing dashboard",
        problem: "Leads, site visits and bookings sit across portals, the CRM and spreadsheets, so nobody trusts the conversion numbers.",
        build:
          "Leads, meetings, site visits and bookings pulled from every channel into one dashboard, with conversion by project, source and team and alerts on leads left without follow-up.",
        outcomes: ["One trusted funnel", "Conversion by source and project", "No lead left untouched"],
      },
    ],
  },
];

const roadmap = [
  { when: "Day 1", title: "Same-day proof", body: "Our senior team comes on site, picks one workflow from your brief and demonstrates a working proof of concept on your data the same evening.", nums: ["01"] },
  { when: "Weeks 2 to 8", title: "Pilot on live projects", body: "Curing, progress and escalation tracking across a set of project groups, measured against the current process.", nums: ["01", "02", "03"] },
  { when: "Months 2 to 4", title: "Clear the paperwork", body: "Gate logs, goods receipts, invoices, contractor bills and compliance documents.", nums: ["05", "06", "09", "10", "11"] },
  { when: "Month 4 onwards", title: "Scale the brain", body: "Company knowledge AI, executive reporting, the control tower and the sales dashboard across the portfolio.", nums: ["13", "14", "15", "16"] },
];

const caseStudies = [
  {
    id: "concrete-curing-automation",
    title: "AI Vision Concrete Curing Automation",
    client: "Leading Infrastructure Firm",
    description: "A WhatsApp vision-language agent across 100+ site groups that verifies concrete curing from photos and replaces manual Excel logging at head office.",
    metric: "100+ WhatsApp groups automated",
  },
  {
    id: "equipment-ordering-app",
    title: "Ordering Platform for Site Equipment",
    client: "Leading Construction Group",
    description: "Replaced phone-based equipment ordering with a mobile catalogue and approvals, cutting delivery errors and connecting site requests to procurement.",
    metric: "Zero wrong equipment deliveries",
  },
  {
    id: "ai-invoicing-engine",
    title: "AI Invoicing Engine",
    client: "Global Enterprise",
    description: "Multi-format, multi-language invoice intake, extraction, validation and posting at enterprise volume.",
    metric: "Invoice backlog eliminated",
  },
];

const faqs = [
  {
    q: "Who is this built for?",
    a: "Real estate developers, EPC and construction companies, and turnkey home builders running many projects in parallel. We are already running these workflows for Bengaluru developers and infrastructure firms, and every workflow can be started on its own.",
  },
  {
    q: "Do our site teams need a new app?",
    a: "No. Site engineers keep posting on WhatsApp exactly as they do today. The agents read the groups they are added to, and office teams keep working in Teams, email, Excel and your ERP.",
  },
  {
    q: "How does the knowledge AI keep information secure?",
    a: "It follows your existing access rules for groups, mailboxes and folders, every answer links to its source, and every query is logged. It can run in your own cloud or Microsoft 365 environment, and your data is never used to train anyone else's models.",
  },
  {
    q: "How accurate is the curing and photo verification?",
    a: "Each result comes with a confidence score. Clear cases are logged automatically and unclear photos or conflicting messages are sent to a person to review, and those corrections improve the rules during the engagement.",
  },
  {
    q: "What does a first engagement look like?",
    a: "You share a one-page brief on one repetitive workflow. Our senior team visits, builds a proof of concept and demonstrates it the same evening, then we pilot it on a set of live projects with a fixed, milestone-based price.",
  },
];

const description =
  "AI workflow automation for construction and real estate: curing verification from WhatsApp photos, daily progress and work plans, client escalation tracking, change requests, gate logs, goods receipts, equipment ordering, offline attendance, invoice and PO matching, contractor billing, RERA and approvals, buyer KYC, company knowledge AI across WhatsApp, Teams, email and files, executive reporting, delay risk and sales dashboards, built by VANCO.";

export default function ConstructionPage() {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "AI Workflow Automation for Construction and Real Estate",
      serviceType: "AI workflow automation for construction companies and real estate developers",
      description,
      url: `${SITE_URL}${PATH}`,
      provider: { "@id": `${SITE_URL}/#organization` },
      areaServed: [{ "@type": "Country", name: "India" }, "Worldwide"],
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Construction workflow automations",
        itemListElement: stages.flatMap((s) =>
          s.offerings.map((o) => ({ "@type": "Offer", itemOffered: { "@type": "Service", name: o.title } }))
        ),
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    },
    breadcrumbs([
      { name: "Home", path: "/" },
      { name: "Construction AI Workflow Automation", path: PATH },
    ]),
  ];

  return (
    <div className="overflow-hidden bg-white text-slate-800" data-testid="industry-page" id="industry-page-construction">
      <SEO title="AI for Construction and Real Estate | VANCO" description={description} path={PATH} jsonLd={jsonLd} />

      {/* Hero */}
      <section className="relative pt-36 pb-20 md:pt-44 md:pb-24 bg-slate-950 text-white overflow-hidden" data-testid="industry-hero">
        <img
          src={`${IMG}/hero.jpg`}
          alt="Site engineers inspecting rebar on a construction site"
          className="absolute inset-0 w-full h-full object-cover object-center pointer-events-none select-none"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-950/85 to-slate-900/60" />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl relative z-10">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-2 bg-white/10 border border-white/15 text-[#f7c550] rounded-full text-xs font-bold tracking-widest uppercase mb-6 font-['Inter']">
              Construction and real estate
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight font-['Manrope'] mb-6 leading-tight text-white">
              Run every project from land to launch on what your teams already share
            </h1>
            <p className="text-lg md:text-xl text-slate-200 leading-relaxed mb-10 font-['Inter']">
              AI agents that read your site WhatsApp groups, Teams, email and files, verify the work, fill the registers and answer leadership's questions. No new apps for site teams, no hardware. Already running on Bengaluru sites.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button size="lg" className="rounded-full px-8 py-6 text-base font-bold bg-[#f7c550] hover:bg-[#f5b82e] text-slate-950 shadow-lg shadow-amber-500/20">
                  Talk to an expert
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href="#offerings">
                <Button size="lg" variant="outline" className="rounded-full px-8 py-6 text-base font-semibold border-white/30 bg-white/5 text-white hover:bg-white/15 hover:text-white">
                  See the 16 workflows
                  <ArrowDown className="ml-2 h-5 w-5" />
                </Button>
              </a>
            </div>
          </div>

          <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {heroStats.map((s) => (
              <div key={s.label} className="rounded-2xl bg-white/5 backdrop-blur border border-white/10 p-5">
                <div className="text-3xl md:text-4xl font-bold font-['Manrope'] text-[#f7c550]">{s.val}</div>
                <div className="text-sm text-slate-200 mt-1 font-['Inter']">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Three levers */}
      <AnimatedSection className="py-24 bg-white" data-testid="construction-levers">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="max-w-3xl mb-14">
            <p className="text-sm font-bold tracking-widest uppercase text-[#243b7f] mb-4 font-['Inter']">Why now</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-[#0f172a] font-['Manrope'] mb-5">
              Great projects still run on manual coordination
            </h2>
            <p className="text-lg text-slate-600 font-['Inter']">
              Photos, chats, registers and spreadsheets are where time, accuracy and visibility quietly leak away. We automate that coordination layer inside the WhatsApp, Teams, email and ERP you already run.
            </p>
          </div>
          <AnimatedContainer className="grid md:grid-cols-3 gap-6">
            {levers.map((l) => {
              const Icon = l.icon;
              return (
                <AnimatedItem key={l.title}>
                  <div className="h-full rounded-3xl border border-slate-200/70 bg-slate-50/60 p-8 hover:shadow-xl hover:shadow-slate-200/60 transition-all duration-300">
                    <div className="w-12 h-12 rounded-2xl bg-[#243b7f] text-[#f7c550] flex items-center justify-center mb-6">
                      <Icon className="w-6 h-6" />
                    </div>
                    <h3 className="text-xl font-bold text-[#0f172a] font-['Manrope'] mb-3">{l.title}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed font-['Inter']">{l.body}</p>
                  </div>
                </AnimatedItem>
              );
            })}
          </AnimatedContainer>
        </div>
      </AnimatedSection>

      {/* Knowledge source */}
      <AnimatedSection className="py-24 bg-[#0f172a] text-white" data-testid="construction-knowledge">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <p className="text-sm font-bold tracking-widest uppercase text-[#f7c550] mb-4 font-['Inter']">One knowledge source</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight font-['Manrope'] mb-5">
                Everything your company knows, answered in seconds
              </h2>
              <p className="text-slate-300 text-lg font-['Inter'] mb-6">
                Every site group, Teams thread, email and file already holds the answer. We connect them into one living knowledge source, and an AI that answers with the source attached, writes the reports and flags what needs attention.
              </p>
              <ul className="space-y-3 font-['Inter'] text-slate-200 text-sm">
                {["Ask on WhatsApp, Teams or the web", "Every answer links to the message, email or file it came from", "Follows your existing access rules"].map((t) => (
                  <li key={t} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#f7c550] shrink-0" />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-7">
              <div className="grid sm:grid-cols-2 gap-4">
                {sources.map((s, i) => {
                  const Icon = s.icon;
                  return (
                    <div key={s.name} className="relative rounded-3xl bg-white/5 border border-white/10 p-6">
                      <div className="w-12 h-12 rounded-2xl bg-[#243b7f] text-[#f7c550] flex items-center justify-center mb-4">
                        <Icon className="w-6 h-6" />
                      </div>
                      <h3 className="text-lg font-bold font-['Manrope'] mb-1">{s.name}</h3>
                      <p className="text-sm text-slate-300 font-['Inter'] pr-6">{s.desc}</p>
                      <span className="absolute top-5 right-6 text-2xl font-light text-white/30">{i < sources.length - 1 ? "+" : "="}</span>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 rounded-3xl bg-[#f7c550] text-slate-950 p-6 flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-slate-950 text-[#f7c550] flex items-center justify-center shrink-0">
                  <BrainCircuit className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-lg font-bold font-['Manrope']">Your company's knowledge source</h3>
                  <p className="text-sm text-slate-800 font-['Inter']">
                    "Why is the Tower C slab pour delayed, and has anyone told the client?" Answered in seconds, with the site group, vendor email and goods receipt register cited.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Stage navigation */}
      <section id="offerings" className="bg-[#243b7f] text-white py-16 scroll-mt-20" data-testid="construction-offerings-nav">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7">
              <p className="text-sm font-bold tracking-widest uppercase text-[#f7c550] mb-4 font-['Inter']">The workflows</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight font-['Manrope']">
                Sixteen workflows, from the site gate to the boardroom
              </h2>
            </div>
            <div className="lg:col-span-5">
              <ul className="flex flex-wrap gap-2">
                {stages.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      className="inline-block px-4 py-2 rounded-full border border-white/20 bg-white/5 hover:bg-[#f7c550] hover:text-slate-950 hover:border-[#f7c550] text-sm font-semibold transition-colors font-['Inter']"
                    >
                      {s.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Stages */}
      {stages.map((stage, si) => (
        <section
          key={stage.id}
          id={stage.id}
          className={`py-24 scroll-mt-20 ${si % 2 === 0 ? "bg-white" : "bg-slate-50/70"} border-t border-slate-100`}
          data-testid={`construction-stage-${stage.id}`}
        >
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
              <div className="lg:col-span-4 lg:sticky lg:top-28">
                <div className="relative rounded-3xl overflow-hidden aspect-[3/2] mb-7 shadow-xl shadow-slate-200/70">
                  <img src={stage.image} alt={stage.label} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/20 to-transparent" />
                  <span className="absolute left-5 bottom-5 inline-block px-3 py-1.5 bg-[#f7c550] text-slate-950 rounded-md text-xs font-bold uppercase tracking-wider font-['Inter']">
                    {stage.kicker}
                  </span>
                </div>
                <p className="text-sm font-bold tracking-widest uppercase text-[#243b7f] mb-3 font-['Inter']">{stage.label}</p>
                <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-[#0f172a] font-['Manrope'] mb-4">{stage.title}</h2>
                <p className="text-slate-600 font-['Inter'] leading-relaxed">{stage.intro}</p>
              </div>

              <div className="lg:col-span-8 space-y-6">
                {stage.offerings.map((o) => {
                  const Icon = o.icon;
                  return (
                    <article
                      key={o.num}
                      className="group rounded-3xl bg-white border border-slate-200/70 p-7 md:p-9 shadow-sm hover:shadow-2xl hover:shadow-slate-200/60 hover:-translate-y-1 transition-all duration-500"
                      data-testid={`construction-offering-${o.num}`}
                    >
                      <div className="flex items-start gap-5">
                        <div className="shrink-0 w-14 h-14 rounded-2xl bg-[#243b7f] text-[#f7c550] flex items-center justify-center">
                          <Icon className="w-7 h-7" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="text-xs font-bold tracking-widest text-[#243b7f] font-['Inter']">{o.num}</span>
                            <span className="h-px flex-1 bg-slate-200" />
                          </div>
                          <h3 className="text-xl md:text-2xl font-bold text-[#0f172a] font-['Manrope'] mb-3 group-hover:text-[#243b7f] transition-colors">
                            {o.title}
                          </h3>
                          <p className="text-sm font-semibold text-red-600/90 mb-4 font-['Inter']">
                            <span className="uppercase tracking-wider text-[11px] mr-2">The problem</span>
                            <span className="text-slate-700 font-normal">{o.problem}</span>
                          </p>
                          <p className="text-slate-600 text-sm md:text-[15px] leading-relaxed font-['Inter'] mb-5">
                            <span className="uppercase tracking-wider text-[11px] font-bold text-[#243b7f] mr-2">What we automate</span>
                            {o.build}
                          </p>
                          <ul className="flex flex-wrap gap-2">
                            {o.outcomes.map((t) => (
                              <li key={t} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-800 text-xs font-semibold font-['Inter'] border border-emerald-100">
                                <CheckCircle2 className="w-3.5 h-3.5" />
                                {t}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Roadmap */}
      <AnimatedSection className="py-24 bg-[#0f172a] text-white relative overflow-hidden" data-testid="construction-roadmap">
        <img src={`${IMG}/roadmap.jpg`} alt="" aria-hidden="true" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-20 pointer-events-none select-none" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0f172a]/80 via-[#0f172a]/90 to-[#0f172a]" />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl relative z-10">
          <div className="max-w-3xl mb-14">
            <p className="text-sm font-bold tracking-widest uppercase text-[#f7c550] mb-4 font-['Inter']">Where to start</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight font-['Manrope'] mb-5">
              Start with one workflow, prove it in a day, then scale
            </h2>
            <p className="text-slate-300 text-lg font-['Inter']">
              Pick your most repetitive process. We show it working on your own data the same day, pilot it on live projects, and every next workflow plugs into the same groups, mailboxes and folders.
            </p>
          </div>
          <AnimatedContainer className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {roadmap.map((r, i) => (
              <AnimatedItem key={r.title}>
                <div className={`h-full rounded-3xl p-7 border ${i === 0 ? "bg-[#f7c550] text-slate-950 border-[#f7c550]" : "bg-white/5 border-white/10"}`}>
                  <div className={`text-xs font-bold tracking-widest uppercase mb-3 font-['Inter'] ${i === 0 ? "text-slate-800" : "text-[#f7c550]"}`}>{r.when}</div>
                  <h3 className="text-xl font-bold font-['Manrope'] mb-3">{r.title}</h3>
                  <p className={`text-sm leading-relaxed font-['Inter'] mb-5 ${i === 0 ? "text-slate-800" : "text-slate-300"}`}>{r.body}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {r.nums.map((n) => (
                      <span key={n} className={`px-2 py-1 rounded-md text-[11px] font-bold font-['Inter'] ${i === 0 ? "bg-slate-950/10 text-slate-900" : "bg-white/10 text-white"}`}>{n}</span>
                    ))}
                  </div>
                </div>
              </AnimatedItem>
            ))}
          </AnimatedContainer>
        </div>
      </AnimatedSection>

      {/* Success stories */}
      <section className="py-24 bg-slate-50/50 border-t border-b border-slate-100" data-testid="industry-case-studies">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="max-w-3xl mb-14">
            <p className="text-sm font-bold tracking-widest uppercase text-[#243b7f] mb-4 font-['Inter']">Delivered work</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-[#0f172a] font-['Manrope']">
              Proven in production on construction sites
            </h2>
          </div>
          <AnimatedContainer className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {caseStudies.map((study) => (
              <AnimatedItem key={study.id}>
                <Link to={`/case-studies/${study.id}`} className="group block h-full">
                  <div className="h-full flex flex-col justify-between min-h-[320px] bg-white border border-slate-200/60 rounded-3xl p-8 shadow-sm hover:shadow-2xl hover:shadow-slate-200/50 hover:-translate-y-2 transition-all duration-500">
                    <div>
                      <div className="inline-block px-3 py-1.5 bg-[#f7c550] text-[#0f172a] font-bold text-xs uppercase tracking-wider rounded-md mb-6">Case Study</div>
                      <h3 className="text-[#0a122c] text-xl font-bold font-['Manrope'] mb-2 leading-snug group-hover:text-[#243b7f] transition-colors">{study.title}</h3>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4 font-['Inter']">{study.client}</p>
                      <p className="text-slate-600 text-sm leading-relaxed font-['Inter'] mb-4">{study.description}</p>
                      <p className="text-[#243b7f] text-sm font-bold font-['Inter']">{study.metric}</p>
                    </div>
                    <span className="text-[#243b7f] font-semibold text-sm hover:underline inline-flex items-center gap-1 mt-6">
                      Read Now
                      <ArrowRight className="w-4 h-4 ml-0.5" />
                    </span>
                  </div>
                </Link>
              </AnimatedItem>
            ))}
          </AnimatedContainer>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-white" data-testid="construction-faq">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl">
          <p className="text-sm font-bold tracking-widest uppercase text-[#243b7f] mb-4 font-['Inter']">FAQ</p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#0f172a] font-['Manrope'] mb-10">Questions construction teams ask us</h2>
          <div className="divide-y divide-slate-200 border-t border-b border-slate-200">
            {faqs.map((f) => (
              <details key={f.q} className="group py-5">
                <summary className="flex items-start justify-between gap-6 cursor-pointer list-none text-lg font-semibold text-[#0f172a] font-['Manrope']">
                  {f.q}
                  <span className="shrink-0 mt-1 w-6 h-6 rounded-full border border-slate-300 flex items-center justify-center text-slate-500 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-slate-600 leading-relaxed font-['Inter'] pr-12">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#243b7f]" data-testid="industry-cta">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white font-['Manrope'] mb-6">
            Let's automate your first workflow
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto font-['Inter']">
            A two-hour workshop with your leadership: we show how we work, map your land-to-launch workflows together and pick one for a same-day proof of concept.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-[#f7c550] hover:bg-[#eab308] text-[#0f172a] rounded-full px-10 py-6 text-lg font-bold transition-all duration-300 hover:scale-105">
              Book the workshop
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
